import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAdminApplications } from '@/features/admin/hooks/useAdminApplications'
import { configureProductionRequirementsFn } from '@/lib/firebase/functions'
import { getCollection, typedCollection, where } from '@/lib/firebase/firestore'
import { formatUserFacingError } from '@/lib/errors'
import type { ApplicationFormDocument, ApplicationField, ProductionDocument } from '@/types'
import type { ParticipantRequirement } from '@/types/productionRequirements'

function localDateTime(iso: string): string {
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

export function ProductionRequirementsSettings({
  productionId,
  production,
  onSaved,
}: {
  productionId: string
  production: ProductionDocument
  onSaved: () => void
}) {
  const { applications } = useAdminApplications()
  const [formId, setFormId] = useState(production.techApplicationId ?? '')
  const [requirements, setRequirements] = useState(production.participantRequirements ?? [])
  const [summaryIds, setSummaryIds] = useState(production.participantSummaryFieldIds ?? [])
  const [replyTo, setReplyTo] = useState(production.replyToEmail ?? '')
  const [fields, setFields] = useState<ApplicationField[]>([])
  const [intakeFields, setIntakeFields] = useState<ApplicationField[]>([])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setFormId(production.techApplicationId ?? '')
    setRequirements(production.participantRequirements ?? [])
    setSummaryIds(production.participantSummaryFieldIds ?? [])
    setReplyTo(production.replyToEmail ?? '')
  }, [production])
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    async function load(id?: string) {
      if (!id) return []
      const forms = await getCollection(
        typedCollection<ApplicationFormDocument>('applicationForms'),
        where('applicationId', '==', id)
      )
      return forms[0]?.sections.flatMap((s) => s.fields) ?? []
    }
    void Promise.all([load(formId), load(production.intakeApplicationId)])
      .then(([next, intake]) => {
        if (!cancelled) {
          setFields(next)
          setIntakeFields(intake)
        }
      })
      .catch((error: unknown) => {
        if (!cancelled)
          setMessage(formatUserFacingError(error, 'Could not load questionnaire fields.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [formId, production.intakeApplicationId])
  function patch(index: number, value: Partial<ParticipantRequirement>) {
    setRequirements((items) => items.map((r, i) => (i === index ? { ...r, ...value } : r)))
  }
  async function save() {
    setSaving(true)
    setMessage('')
    try {
      await configureProductionRequirementsFn({
        productionId,
        techApplicationId: formId,
        requirements,
        summaryFieldIds: summaryIds,
        replyToEmail: replyTo,
        expectedVersion: production.participantRequirementsVersion ?? 0,
      })
      setMessage('Required materials saved.')
      onSaved()
    } catch (error) {
      setMessage(formatUserFacingError(error, 'Could not save required materials.'))
    } finally {
      setSaving(false)
    }
  }
  const selectClass = 'w-full rounded-md border bg-background p-2 text-sm'
  return (
    <Card>
      <CardHeader>
        <CardTitle>Required participant materials</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Artists submit through their invitation link. Staff download and review each delivery
          before it is marked verified.
        </p>
        <div className="space-y-2">
          <Label htmlFor="materials-questionnaire">Questionnaire</Label>
          <select
            id="materials-questionnaire"
            className={selectClass}
            value={formId}
            onChange={(e) => {
              setFormId(e.target.value)
              setRequirements([])
            }}
          >
            <option value="">None</option>
            {applications
              .filter((a) => a.applicationType === 'tech_questionnaire')
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
          </select>
          {formId && (
            <Link className="text-sm underline" to={`/admin/applications/${formId}/edit`}>
              Edit questionnaire and its required URL fields
            </Link>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="materials-reply-to">Reply address</Label>
          <Input
            id="materials-reply-to"
            type="email"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
          />
        </div>
        {requirements.map((r, i) => (
          <fieldset key={r.id} className="space-y-3 rounded border p-4">
            <legend className="px-1 text-sm font-medium">Material {i + 1}</legend>
            <Label htmlFor={`requirement-label-${i}`}>Name shown to the artist</Label>
            <Input
              id={`requirement-label-${i}`}
              value={r.label}
              onChange={(e) => patch(i, { label: e.target.value })}
            />
            <Label htmlFor={`requirement-field-${i}`}>Download-link field</Label>
            <select
              id={`requirement-field-${i}`}
              className={selectClass}
              value={r.fieldId}
              onChange={(e) => patch(i, { fieldId: e.target.value })}
            >
              <option value="">Select URL field</option>
              {fields
                .filter((f) => f.type === 'url')
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                    {f.required ? ' (required)' : ''}
                  </option>
                ))}
            </select>
            <Label htmlFor={`requirement-access-${i}`}>Access instructions field</Label>
            <select
              id={`requirement-access-${i}`}
              className={selectClass}
              value={r.accessFieldId ?? ''}
              onChange={(e) => patch(i, { accessFieldId: e.target.value || undefined })}
            >
              <option value="">None</option>
              {fields
                .filter((f) => ['short_text', 'long_text'].includes(f.type))
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
            </select>
            <Label htmlFor={`requirement-due-${i}`}>
              Deadline (device time: {Intl.DateTimeFormat().resolvedOptions().timeZone})
            </Label>
            <Input
              id={`requirement-due-${i}`}
              type="datetime-local"
              value={localDateTime(r.dueAt)}
              onChange={(e) => {
                if (e.target.value) patch(i, { dueAt: new Date(e.target.value).toISOString() })
              }}
            />
            <Label htmlFor={`requirement-zone-${i}`}>Timezone displayed to artists</Label>
            <Input
              id={`requirement-zone-${i}`}
              value={r.timeZone}
              onChange={(e) => patch(i, { timeZone: e.target.value })}
            />
            <Label htmlFor={`requirement-instructions-${i}`}>Delivery instructions</Label>
            <Textarea
              id={`requirement-instructions-${i}`}
              value={r.instructions}
              onChange={(e) => patch(i, { instructions: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={r.required}
                onChange={(e) => patch(i, { required: e.target.checked })}
              />{' '}
              Required before the participant is ready
            </label>
            <Button
              variant="outline"
              onClick={() => setRequirements((items) => items.filter((_, n) => n !== i))}
            >
              Remove requirement
            </Button>
          </fieldset>
        ))}
        <Button
          variant="outline"
          disabled={!formId || loading || requirements.length >= 10}
          onClick={() =>
            setRequirements((items) => [
              ...items,
              {
                id: crypto.randomUUID(),
                kind: 'downloadable_file',
                label: '',
                fieldId: '',
                required: true,
                dueAt: new Date(Date.now() + 7 * 86400000).toISOString(),
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                instructions: '',
              },
            ])
          }
        >
          Add required material
        </Button>
        {!!requirements.length && (
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">
              Intake details to display with the delivery form
            </legend>
            {intakeFields
              .filter((f) => ['short_text', 'number'].includes(f.type))
              .map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={summaryIds.includes(f.id)}
                    onChange={(e) =>
                      setSummaryIds((ids) =>
                        e.target.checked ? [...ids, f.id] : ids.filter((id) => id !== f.id)
                      )
                    }
                  />
                  {f.label}
                </label>
              ))}
          </fieldset>
        )}
        <p className="text-xs text-muted-foreground">
          Changing requirements creates a new version. Previously delivered materials must be
          submitted and reviewed against that version.
        </p>
        {message && (
          <p role="status" className="text-sm">
            {message}
          </p>
        )}
        <Button disabled={saving || loading} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save required materials'}
        </Button>
      </CardContent>
    </Card>
  )
}
